import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "../axiosConfig";
import { toast } from "react-toastify";
import { btn } from "../lib/ui";
import { useEdgeSwipe } from "../hooks/useEdgeSwipe";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import TodoItem from "../components/TodoItem";
import TodoModal from "../components/TodoModal";
import SearchFilter from "../components/SearchFilter";
import CalendarPanel from "../components/CalendarPanel";
import StatsStrip from "../components/StatsStrip";
import BulkActionToolbar from "../components/BulkActionToolbar";
import DashboardView from "../components/DashboardView";
import DocumentVault from "../components/DocumentVault";
import PersonalReminderModal from "../components/PersonalReminderModal";
import ConfirmDialog from "../components/ConfirmDialog";
import NotesPanel from "../components/NotesPanel";
import TrashPanel from "../components/TrashPanel";
import CommandPalette from "../components/CommandPalette";
import SubjectNotesPanel from "../components/SubjectNotesPanel";
import DatesheetPanel from "../components/DatesheetPanel";
import SyllabusPanel from "../components/SyllabusPanel";
import OfflineBanner from "../components/OfflineBanner";
import { isToday, isPast } from "date-fns";
import { getNotificationPermissionStatus, scheduleExactReminder, scheduleTaskReminder } from "../utils/notifications";
import { cacheSnapshot, getCachedSnapshot, isOnline, trySync, updateTodoOffline, deleteTodoOffline } from "../utils/offlineSync";
import { getQueue } from "../utils/offlineDb";

interface Todo {
  _id: string;
  title: string;
  description?: string;
  completed: boolean;
  priority: "low" | "medium" | "high";
  category: string;
  dueDate?: string;
  tags?: string[];
  subject?: string;
  assignee?: { _id: string; name?: string; email: string } | string | null;
  subtasks?: { title: string; completed: boolean }[];
  attachments?: { _id: string; title: string }[];
  recurrence?: { freq: "daily" | "weekly" | "monthly"; interval: number; until?: string } | null;
  createdAt: string;
}

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const createReminderId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

interface Subject {
  _id: string;
  name: string;
  icon: string;
  color: string;
}

interface PersonalReminder {
  id: string;
  title: string;
  dateTime: string;
  notes: string;
}

const PERSONAL_REMINDERS_KEY = "taskflow-personal-reminders";
const SUBJECT_NOTES_PREFIX = "subject_notes_";

export default function HomePage() {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [activeView, setActiveView] = useState("dashboard");
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEdgeSwipe({ isOpen: sidebarOpen, onOpen: () => setSidebarOpen(true), onClose: () => setSidebarOpen(false) });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sort, setSort] = useState("dueDate");
  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showPersonalReminderModal, setShowPersonalReminderModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Todo | null>(null);
  const [personalReminders, setPersonalReminders] = useState<PersonalReminder[]>(() => {
    try {
      const saved = localStorage.getItem(PERSONAL_REMINDERS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [reminderMinutes, setReminderMinutes] = useState(() => {
    const saved = localStorage.getItem("reminderMinutesBefore");
    return saved ? Number(saved) : 15;
  });
  const [notificationReady, setNotificationReady] = useState(false);
  const scheduledRef = useRef<number[]>([]);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/todos");
      setTodos(res.data);
      setIsOffline(false);
      cacheSnapshot("todos", res.data);
    } catch {
      if (!isOnline()) {
        const cached = await getCachedSnapshot("todos");
        if (cached) {
          setTodos(cached);
          setIsOffline(true);
        } else {
          toast.error("Failed to fetch todos");
        }
      } else {
        toast.error("Failed to fetch todos");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const res = await axios.get("/subjects");
      setSubjects(res.data);
    } catch {
      console.error("Failed to fetch subjects");
    }
  };

  useEffect(() => {
    fetchTodos();
    fetchSubjects();
  }, []);

  useEffect(() => {
    const handleSubjectsChanged = () => {
      fetchSubjects();
    };

    window.addEventListener("subjects:changed", handleSubjectsChanged);
    return () => window.removeEventListener("subjects:changed", handleSubjectsChanged);
  }, []);

  const refreshPendingSyncCount = async () => {
    const queue = await getQueue();
    setPendingSyncCount(queue.length);
  };

  const runSync = async () => {
    const { synced, remaining } = await trySync();
    setPendingSyncCount(remaining);
    if (synced > 0) {
      toast.success(`Synced ${synced} offline change${synced > 1 ? "s" : ""}`);
      await fetchTodos();
    }
    setIsOffline(!isOnline());
  };

  useEffect(() => {
    refreshPendingSyncCount();
    setIsOffline(!isOnline());
    if (isOnline()) runSync();

    const handleOnline = () => runSync();
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("reminderMinutesBefore", String(reminderMinutes));
  }, [reminderMinutes]);

  useEffect(() => {
    localStorage.setItem(PERSONAL_REMINDERS_KEY, JSON.stringify(personalReminders));
  }, [personalReminders]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const todoCounts = useMemo(() => ({
    inbox:     todos.filter(t => !t.completed).length,
    today:     todos.filter(t => t.dueDate && isToday(new Date(t.dueDate)) && !t.completed).length,
    upcoming:  todos.filter(t => t.dueDate && !isToday(new Date(t.dueDate)) && !isPast(new Date(t.dueDate)) && !t.completed).length,
    completed: todos.filter(t => t.completed).length,
  }), [todos]);

  const reminderTodos = useMemo(() => {
    const now = Date.now();
    const in24h = now + 24 * 60 * 60 * 1000;
    return todos.filter((t) => {
      if (!t.dueDate || t.completed) return false;
      const due = new Date(t.dueDate).getTime();
      return due >= now && due <= in24h;
    });
  }, [todos]);

  useEffect(() => {
    setNotificationReady(getNotificationPermissionStatus() === "granted");
  }, []);

  useEffect(() => {
    scheduledRef.current.forEach((id) => window.clearTimeout(id));
    scheduledRef.current = [];

    if (!notificationReady) return;

    reminderTodos.forEach((todo) => {
      if (!todo.dueDate) return;
      const timeoutId = scheduleTaskReminder(todo.title, new Date(todo.dueDate), reminderMinutes);
      if (timeoutId) scheduledRef.current.push(timeoutId as unknown as number);
    });

    personalReminders.forEach((reminder) => {
      const timeoutId = scheduleExactReminder(reminder.title, new Date(reminder.dateTime), reminder.notes || undefined);
      if (timeoutId) scheduledRef.current.push(timeoutId as unknown as number);
    });

    return () => {
      scheduledRef.current.forEach((id) => window.clearTimeout(id));
      scheduledRef.current = [];
    };
  }, [reminderTodos, reminderMinutes, personalReminders, notificationReady]);

  const getFilteredTodos = () => {
    let filtered = [...todos];

    if (activeView.startsWith("subject_") && !activeView.startsWith(SUBJECT_NOTES_PREFIX)) {
      const subjectId = activeView.replace("subject_", "");
      filtered = filtered.filter(t => t.subject === subjectId);
    } else if (activeView === "today") {
      filtered = filtered.filter(t => t.dueDate && isToday(new Date(t.dueDate)) && !t.completed);
    } else if (activeView === "upcoming") {
      filtered = filtered.filter(t => t.dueDate && !isToday(new Date(t.dueDate)) && !isPast(new Date(t.dueDate)) && !t.completed);
    } else if (activeView === "completed") {
      filtered = filtered.filter(t => t.completed);
    } else if (activeView === "calendar") {
      filtered = filtered.filter(t => !t.completed);
    } else if (activeView === "reminders") {
      filtered = filtered.filter((t) => {
        if (!t.dueDate || t.completed) return false;
        const due = new Date(t.dueDate).getTime();
        const now = Date.now();
        return due >= now && due <= now + 24 * 60 * 60 * 1000;
      });
    } else {
      filtered = filtered.filter(t => !t.completed);
    }

    if (search) filtered = filtered.filter(t => t.title.toLowerCase().includes(search.toLowerCase()));
    if (priorityFilter) filtered = filtered.filter(t => t.priority === priorityFilter);
    if (categoryFilter) filtered = filtered.filter(t => t.category === categoryFilter);

    filtered.sort((a, b) => {
      if (sort === "dueDate") {
        return (a.dueDate ? new Date(a.dueDate).getTime() : Infinity) -
               (b.dueDate ? new Date(b.dueDate).getTime() : Infinity);
      }
      if (sort === "priority") {
        const o: Record<string, number> = { high: 1, medium: 2, low: 3 };
        return (o[a.priority] || 4) - (o[b.priority] || 4);
      }
      if (sort === "newest") return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sort === "oldest") return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return 0;
    });

    return filtered;
  };

  const toggleComplete = async (id: string, completed: boolean) => {
    try {
      const { synced } = await updateTodoOffline(id, { completed });
      setTodos(todos.map(t => t._id === id ? { ...t, completed } : t));
      if (synced) {
        toast.success(completed ? "Task completed!" : "Task reopened");
      } else {
        toast.info("You're offline - this will sync once you're back online");
        await refreshPendingSyncCount();
      }
    } catch { toast.error("Failed to update task"); }
  };

  const requestDeleteTodo = (id: string) => {
    setDeleteTarget(todos.find(t => t._id === id) || null);
  };

  const confirmDeleteTodo = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget._id;
    setDeleteTarget(null);
    try {
      const { synced } = await deleteTodoOffline(id);
      setTodos(todos.filter(t => t._id !== id));
      if (synced) {
        toast.success("Task moved to trash");
      } else {
        toast.info("You're offline - this delete will sync once you're back online");
        await refreshPendingSyncCount();
      }
    } catch { toast.error("Failed to delete task"); }
  };

  const toggleSelectMode = () => {
    setSelectMode((v) => !v);
    setSelectedIds([]);
  };

  const toggleSelectTodo = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectMode(false);
  };

  const handleBulkComplete = async () => {
    try {
      await axios.patch("/todos/bulk", { ids: selectedIds, update: { completed: true } });
      toast.success(`${selectedIds.length} task(s) completed`);
      clearSelection();
      await fetchTodos();
    } catch { toast.error("Failed to update tasks"); }
  };

  const handleBulkMove = async (subjectId: string) => {
    try {
      await axios.patch("/todos/bulk", { ids: selectedIds, update: { subject: subjectId } });
      toast.success(`${selectedIds.length} task(s) moved`);
      clearSelection();
      await fetchTodos();
    } catch { toast.error("Failed to move tasks"); }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.delete("/todos/bulk", { data: { ids: selectedIds } });
      toast.success(`${selectedIds.length} task(s) moved to trash`);
      clearSelection();
      await fetchTodos();
    } catch { toast.error("Failed to delete tasks"); }
  };

  const handleBulkExport = async () => {
    try {
      const res = await axios.get(`/todos/export.ics?ids=${selectedIds.join(",")}`, { responseType: "blob" });
      downloadIcsBlob(res.data);
    } catch { toast.error("Failed to export tasks"); }
  };

  const handleExportAll = async () => {
    try {
      const res = await axios.get("/todos/export.ics", { responseType: "blob" });
      downloadIcsBlob(res.data);
    } catch { toast.error("Failed to export tasks"); }
  };

  const downloadIcsBlob = (data: BlobPart) => {
    const blob = new Blob([data], { type: "text/calendar" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "taskflow.ics";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleEditTodo = (todo: Todo) => {
    setEditingTodo({ ...todo });
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingTodo(null);
    setIsModalOpen(true);
  };

  const handleAddReminder = () => {
    setShowPersonalReminderModal(true);
  };

  const handleSavePersonalReminder = (reminder: { title: string; dateTime: string; notes: string }) => {
    const nextReminder: PersonalReminder = {
      id: createReminderId(),
      title: reminder.title,
      dateTime: reminder.dateTime,
      notes: reminder.notes,
    };

    setPersonalReminders((prev) => [nextReminder, ...prev]);
    toast.success("Reminder saved");
  };

  const handleDeletePersonalReminder = (id: string) => {
    setPersonalReminders((prev) => prev.filter((reminder) => reminder.id !== id));
  };

  const handleSaveTodo = async () => { await fetchTodos(); await refreshPendingSyncCount(); };

  const handleViewChange = (view: string) => {
    setActiveView(view);
    setSelectedSubject(null);
    setSearch("");
    setPriorityFilter("");
    setCategoryFilter("");
    setSidebarOpen(false);
  };

  const handleSubjectSelect = (subjectId: string) => {
    setActiveView(`subject_${subjectId}`);
    setSelectedSubject(subjectId);
    setSidebarOpen(false);
  };

  const handleSubjectNotesOpen = (subjectId: string) => {
    setActiveView(`${SUBJECT_NOTES_PREFIX}${subjectId}`);
    setSidebarOpen(false);
  };

  const getSubjectName = (subjectId: string | undefined) => {
    if (!subjectId) return null;
    return subjects.find(s => s._id === subjectId)?.name || null;
  };

  const getSubjectMeta = (subjectId: string | undefined) => {
    if (!subjectId) return null;
    return subjects.find(s => s._id === subjectId) || null;
  };

  const getAssigneeLabel = (assignee: Todo["assignee"]) => {
    if (!assignee || typeof assignee === "string") return null;
    return assignee.name || assignee.email || null;
  };

  const filteredTodos = getFilteredTodos();

  const viewTitles: Record<string, { label: string; desc: string }> = {
    dashboard: { label: "Dashboard", desc: "Your day at a glance" },
    inbox:     { label: "Inbox",     desc: "All active tasks" },
    today:     { label: "Today",     desc: "Tasks due today" },
    upcoming:  { label: "Upcoming",  desc: "Future tasks" },
    completed: { label: "Completed", desc: "Finished tasks" },
    calendar:  { label: "Calendar",  desc: "Monthly deadlines and due tasks" },
    reminders: { label: "Reminders", desc: "Tasks due in the next 24 hours" },
    vault: { label: "Document Vault", desc: "Upload and manage images and PDFs" },
    notes: { label: "Notes", desc: "Free-form notes and ideas" },
    datesheet: { label: "Datesheet", desc: "Your current exam schedule" },
    syllabus: { label: "Syllabus", desc: "Date-wise syllabus, per subject or combined" },
    trash: { label: "Trash", desc: "Deleted tasks, kept until you remove them for good" },
  };

  const isSubjectNotesView = activeView.startsWith(SUBJECT_NOTES_PREFIX);
  const activeSubjectNotesId = isSubjectNotesView ? activeView.replace(SUBJECT_NOTES_PREFIX, "") : null;

  const viewInfo = isSubjectNotesView
    ? { label: `${getSubjectName(activeSubjectNotesId || undefined) || "Subject"} Notes`, desc: "Notes for this subject" }
    : viewTitles[activeView] || { label: "Subject", desc: "Subject tasks" };
  const showCalendarPreview = ["inbox", "today", "upcoming", "completed"].includes(activeView) || (activeView.startsWith("subject_") && !isSubjectNotesView);
  const nonTaskViews = ["dashboard", "vault", "notes", "datesheet", "syllabus", "trash"];
  const hasCalendarWidget = activeView === "calendar" || activeView === "reminders" || showCalendarPreview;

  const stats = useMemo(() => {
    const overdueCount = todos.filter(t =>
      t.dueDate && isPast(new Date(t.dueDate)) && !isToday(new Date(t.dueDate)) && !t.completed
    ).length;
    const rate = todos.length > 0 ? Math.round((todoCounts.completed / todos.length) * 100) : 0;
    return { overdueCount, rate };
  }, [todos, todoCounts]);

  return (
    <div className="flex flex-col h-[100dvh] bg-page overflow-hidden">
      <Navbar
        onClockClick={() => handleViewChange("calendar")}
        onMenuClick={() => setSidebarOpen((v) => !v)}
        menuOpen={sidebarOpen}
      />

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden lg:block">
          <Sidebar
            activeView={activeView}
            onViewChange={handleViewChange}
            onSubjectSelect={handleSubjectSelect}
            onSubjectNotesOpen={handleSubjectNotesOpen}
            todoCounts={todoCounts}
            reminderCount={reminderTodos.length}
          />
        </div>

        <div
          className={`fixed inset-0 z-40 lg:hidden transition ${sidebarOpen ? "block" : "hidden"}`}
          aria-hidden={!sidebarOpen}
        >
          <div
            className={`absolute inset-0 bg-black/55 transition-opacity duration-300 ease-out ${sidebarOpen ? "opacity-100" : "opacity-0"}`}
            onClick={() => setSidebarOpen(false)}
          />
          <div className={`absolute left-0 top-0 h-full shadow-2xl shadow-black/50 transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
            <Sidebar
              activeView={activeView}
              onViewChange={handleViewChange}
              onSubjectSelect={handleSubjectSelect}
              onSubjectNotesOpen={handleSubjectNotesOpen}
              todoCounts={todoCounts}
              reminderCount={reminderTodos.length}
              forceExpanded
            />
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden bg-page">
          <div className="flex-1 overflow-y-auto">
            <OfflineBanner isOffline={isOffline} pendingSyncCount={pendingSyncCount} offlineLabel="showing cached tasks" />
            <div className="px-4 sm:px-6 pt-4 sm:pt-5">
              <div className={`flex flex-wrap items-center gap-3 sm:gap-4 text-sm font-semibold ${activeView === "dashboard" ? "hidden" : hasCalendarWidget ? "lg:hidden" : ""}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                    <span className="text-muted">{todos.length} total</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                    <span className="text-muted">{todoCounts.completed} done</span>
                  </div>
                  {stats.overdueCount > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-red-500 inline-block animate-pulse" />
                      <span className="text-red-400 font-semibold">{stats.overdueCount} overdue</span>
                    </div>
                  )}
                  {todos.length > 0 && (
                    <div className="flex items-center gap-2 ml-0 sm:ml-auto w-full sm:w-auto">
                      <div className="w-full sm:w-28 h-1.5 bg-surface-alt rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full transition-all duration-700"
                          style={{ width: `${stats.rate}%` }}
                        />
                      </div>
                      <span className="text-muted tabular-nums text-base font-bold">{stats.rate}%</span>
                    </div>
                  )}
              </div>

              {activeView !== "dashboard" && (
                <div className="mt-3 mb-4">
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-text tracking-tight">{viewInfo.label}</h2>
                </div>
              )}

              {activeView !== "calendar" && !nonTaskViews.includes(activeView) && !isSubjectNotesView && (
                <div className={`flex items-start gap-2 ${activeView === "reminders" ? "max-w-2xl" : ""}`}>
                  <div className="flex-1 min-w-0">
                    <SearchFilter
                      onSearch={setSearch}
                      onFilterPriority={setPriorityFilter}
                      onFilterCategory={setCategoryFilter}
                      onSort={setSort}
                      totalTodos={todos.length}
                    />
                  </div>
                  {showCalendarPreview && (
                    <button
                      onClick={toggleSelectMode}
                      className={`shrink-0 rounded-lg px-3 py-2.5 text-sm font-semibold border transition ${
                        selectMode
                          ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                          : "bg-surface-alt text-muted border-border-strong hover:border-border-strong hover:text-text"
                      }`}
                    >
                      {selectMode ? "Done" : "Select"}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div key={activeView} className="p-4 sm:p-6 pt-4 animate-fadeSlideDown">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <div className="w-8 h-8 border-2 border-border border-t-amber-500 rounded-full animate-spin" />
                <p className="text-muted text-sm">Loading...</p>
              </div>
            ) : activeView === "dashboard" ? (
              <DashboardView
                todos={todos}
                subjects={subjects}
                todoCounts={todoCounts}
                overdueCount={stats.overdueCount}
                next24h={reminderTodos.length}
                getSubjectName={getSubjectName}
                onEdit={handleEditTodo}
                onDelete={requestDeleteTodo}
                onToggle={toggleComplete}
                onAddTask={handleAddNew}
                onNavigate={handleViewChange}
              />
            ) : activeView === "calendar" ? (
              <div className="w-full space-y-4">
                <StatsStrip today={todoCounts.today} overdue={stats.overdueCount} next24h={reminderTodos.length} />
                <div className="flex justify-end">
                  <button
                    onClick={handleExportAll}
                    className="rounded-lg bg-surface-alt px-3 py-2 text-xs font-semibold text-text hover:bg-border-strong transition"
                  >
                    Export calendar (.ics)
                  </button>
                </div>
                <CalendarPanel todos={todos} subjects={subjects} />
              </div>
            ) : activeView === "reminders" ? (
              <div className="max-w-5xl space-y-4">
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-start">
                  <div className="space-y-3">
                    <div className="rounded-xl border border-border bg-surface p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-bold text-text">Remind me before due date</h3>
                          <p className="text-xs text-muted mt-1">
                            {notificationReady
                              ? "Browser notifications are on for this device"
                              : "Turn on browser notifications in Settings to see these"}
                          </p>
                        </div>
                        {!notificationReady && (
                          <Link
                            to="/settings"
                            className="rounded-lg bg-surface-alt px-3 py-2 text-xs font-semibold text-text hover:bg-border-strong transition whitespace-nowrap"
                          >
                            Go to Settings
                          </Link>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex flex-wrap gap-2">
                          {[5, 10, 15, 30, 60].map((m) => (
                            <button
                              key={m}
                              onClick={() => {
                                setReminderMinutes(m);
                                toast.success(`Reminder set to ${m} minutes before due time`);
                              }}
                              className={`rounded-lg border px-3 py-1.5 text-sm font-semibold transition ${
                                reminderMinutes === m
                                  ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
                                  : "border-border-strong bg-surface-alt text-text hover:border-border-strong"
                              }`}
                            >
                              {m}m
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-border bg-surface p-4">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div>
                          <h3 className="text-sm font-bold text-text">Personal reminders</h3>
                          <p className="text-xs text-muted mt-1">Create reminders for a specific date and time.</p>
                        </div>
                        <button
                          onClick={handleAddReminder}
                          className="rounded-lg bg-amber-500 px-3 py-2 text-xs font-bold text-black hover:bg-amber-400"
                        >
                          Add reminder
                        </button>
                      </div>

                      {personalReminders.length > 0 ? (
                        <div className="space-y-2">
                          {personalReminders.map((reminder) => (
                            <div key={reminder.id} className="rounded-lg border border-border bg-page px-3 py-2.5 flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-text truncate">{reminder.title}</p>
                                <p className="text-xs text-muted mt-0.5">{new Date(reminder.dateTime).toLocaleString()}</p>
                                {reminder.notes && <p className="text-xs text-muted mt-1 line-clamp-2">{reminder.notes}</p>}
                              </div>
                              <button
                                onClick={() => handleDeletePersonalReminder(reminder.id)}
                                className="rounded-md bg-surface-alt px-2.5 py-1.5 text-xs text-text hover:bg-border-strong shrink-0"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted">No personal reminders yet.</p>
                      )}
                    </div>

                    <div className="rounded-xl border border-border bg-surface p-4">
                      <p className="text-sm font-semibold text-text mb-2">Due reminders</p>
                      {filteredTodos.length > 0 ? (
                        <div className="space-y-2">
                          {filteredTodos.map(todo => (
                            <TodoItem
                              key={todo._id}
                              todo={todo}
                              subjectName={getSubjectName(todo.subject)}
                              subjectColor={getSubjectMeta(todo.subject)?.color}
                              subjectIcon={getSubjectMeta(todo.subject)?.icon}
                              assigneeLabel={getAssigneeLabel(todo.assignee)}
                              onEdit={handleEditTodo}
                              onDelete={requestDeleteTodo}
                              onToggle={toggleComplete}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted">No reminders due in the next 24 hours.</p>
                      )}
                    </div>
                  </div>
                  <div className="hidden lg:block sticky top-4 space-y-3">
                    <StatsStrip today={todoCounts.today} overdue={stats.overdueCount} next24h={reminderTodos.length} />
                    <CalendarPanel todos={todos} subjects={subjects} compact />
                  </div>
                </div>
              </div>
            ) : activeView === "vault" ? (
              <DocumentVault />
            ) : activeView === "notes" ? (
              <NotesPanel />
            ) : activeView === "datesheet" ? (
              <DatesheetPanel />
            ) : activeView === "syllabus" ? (
              <SyllabusPanel />
            ) : isSubjectNotesView && activeSubjectNotesId ? (
              <SubjectNotesPanel
                subjectId={activeSubjectNotesId}
                subjectName={getSubjectName(activeSubjectNotesId) || "Subject"}
              />
            ) : activeView === "trash" ? (
              <TrashPanel />
            ) : showCalendarPreview ? (
              <div className="w-full lg:grid lg:grid-cols-[minmax(360px,40%)_minmax(520px,60%)] lg:items-start lg:gap-4">
                {filteredTodos.length > 0 ? (
                  <div className="space-y-1.5 w-full">
                    {selectedIds.length > 0 && (
                      <BulkActionToolbar
                        count={selectedIds.length}
                        subjects={subjects}
                        onComplete={handleBulkComplete}
                        onMove={handleBulkMove}
                        onDelete={handleBulkDelete}
                        onExport={handleBulkExport}
                        onClear={clearSelection}
                      />
                    )}
                    {filteredTodos.map(todo => (
                      <TodoItem
                        key={todo._id}
                        todo={todo}
                        subjectName={getSubjectName(todo.subject)}
                        subjectColor={getSubjectMeta(todo.subject)?.color}
                        subjectIcon={getSubjectMeta(todo.subject)?.icon}
                        assigneeLabel={getAssigneeLabel(todo.assignee)}
                        onEdit={handleEditTodo}
                        onDelete={requestDeleteTodo}
                        onToggle={toggleComplete}
                        selectable={selectMode}
                        selected={selectedIds.includes(todo._id)}
                        onSelectToggle={toggleSelectTodo}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full min-h-[50vh] gap-4 px-2 w-full">
                    <div className="w-16 h-16 rounded-2xl bg-surface border border-border flex items-center justify-center">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <rect x="4" y="6" width="20" height="18" rx="3" stroke="#52525b" strokeWidth="1.5" />
                        <path d="M9 14h10M9 18h7" stroke="#52525b" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M9 6V4M19 6V4" stroke="#52525b" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </div>
                    <div className="text-center">
                      <p className="text-muted font-semibold">
                        {search ? "No tasks match your search" : activeView === "completed" ? "No completed tasks" : "All clear"}
                      </p>
                      <p className="text-muted text-sm mt-1">
                        {search ? "Try a different search term" : activeView !== "completed" ? "Add a task to get started" : "Complete some tasks first"}
                      </p>
                    </div>
                    {!search && activeView !== "completed" && (
                      <button
                        onClick={handleAddNew}
                        className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm ${btn.cta}`}
                      >
                        <PlusIcon />
                        Add Task
                      </button>
                    )}
                  </div>
                )}

                <div className="hidden lg:block sticky top-3 space-y-3">
                  <StatsStrip today={todoCounts.today} overdue={stats.overdueCount} next24h={reminderTodos.length} />
                  <CalendarPanel todos={todos} subjects={subjects} compact />
                </div>
              </div>
            ) : filteredTodos.length > 0 ? (
              <div className="space-y-1.5 max-w-3xl">
                {filteredTodos.map(todo => (
                  <TodoItem
                    key={todo._id}
                    todo={todo}
                    subjectName={getSubjectName(todo.subject)}
                    subjectColor={getSubjectMeta(todo.subject)?.color}
                    subjectIcon={getSubjectMeta(todo.subject)?.icon}
                    assigneeLabel={getAssigneeLabel(todo.assignee)}
                    onEdit={handleEditTodo}
                    onDelete={requestDeleteTodo}
                    onToggle={toggleComplete}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-4 px-2">
                <div className="w-16 h-16 rounded-2xl bg-surface border border-border flex items-center justify-center">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <rect x="4" y="6" width="20" height="18" rx="3" stroke="#52525b" strokeWidth="1.5" />
                    <path d="M9 14h10M9 18h7" stroke="#52525b" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M9 6V4M19 6V4" stroke="#52525b" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-muted font-semibold">
                    {search ? "No tasks match your search" : activeView === "completed" ? "No completed tasks" : "All clear"}
                  </p>
                  <p className="text-muted text-sm mt-1">
                    {search ? "Try a different search term" : activeView !== "completed" ? "Add a task to get started" : "Complete some tasks first"}
                  </p>
                </div>
                {!search && activeView !== "completed" && (
                  <button
                    onClick={handleAddNew}
                    className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm ${btn.cta}`}
                  >
                    <PlusIcon />
                    Add Task
                  </button>
                )}
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      <TodoModal
        isOpen={isModalOpen}
        todo={editingTodo}
        onClose={() => { setIsModalOpen(false); setEditingTodo(null); }}
        onSave={handleSaveTodo}
        defaultSubject={selectedSubject}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete task"
        message={deleteTarget ? `"${deleteTarget.title}" will move to Trash. You can restore it any time until you delete it for good.` : ""}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDeleteTodo}
        onCancel={() => setDeleteTarget(null)}
      />

      <PersonalReminderModal
        isOpen={showPersonalReminderModal}
        onSave={handleSavePersonalReminder}
        onClose={() => setShowPersonalReminderModal(false)}
      />

      <CommandPalette
        subjects={subjects}
        onViewChange={handleViewChange}
        onSubjectSelect={handleSubjectSelect}
        onNewTask={handleAddNew}
        onOpenTodo={handleEditTodo}
      />
    </div>
  );
}
